import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { formatCurrency, CLIENT_TYPES, PAYMENT_METHODS, RECEIVING_BANKS, CURRENCIES, downloadCSV } from '../lib/utils';
import { Plus, Upload, Download, Pencil, Trash2, Loader2, Search, Filter, X, FileDown, MessageSquare, User } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ClientsPage() {
  const { canEdit } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedNotes, setSelectedNotes] = useState('');
  const [formData, setFormData] = useState({
    client_name: '',
    active: true,
    type: 'Client',
    web_design_amount: '',
    seo_amount: '',
    google_ads_amount: '',
    meta_ads_amount: '',
    contract_currency: 'INR',
    payment_method: 'Bank Transfer',
    receiving_bank: 'SBI',
    client_representative: '',
    representative_email: '',
    representative_phone: '',
    notes: ''
  });
  const [filters, setFilters] = useState({
    active: '',
    type: '',
    search: ''
  });
  const [saving, setSaving] = useState(false);

  // Calculate contract amount from 4 service amounts
  const calculateContractAmount = () => {
    return (
      (parseFloat(formData.web_design_amount) || 0) +
      (parseFloat(formData.seo_amount) || 0) +
      (parseFloat(formData.google_ads_amount) || 0) +
      (parseFloat(formData.meta_ads_amount) || 0)
    );
  };

  const fetchClients = useCallback(async () => {
    try {
      const params = {};
      if (filters.active !== '') params.active = filters.active === 'true';
      if (filters.type) params.type = filters.type;

      const response = await axios.get(`${API_URL}/clients`, { params });
      let data = response.data;
      
      if (filters.search) {
        data = data.filter(c => 
          c.client_name.toLowerCase().includes(filters.search.toLowerCase())
        );
      }
      
      setClients(data);
    } catch (error) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const openCreateDialog = () => {
    setSelectedClient(null);
    setFormData({
      client_name: '',
      active: true,
      type: 'Client',
      web_design_amount: '',
      seo_amount: '',
      google_ads_amount: '',
      meta_ads_amount: '',
      contract_currency: 'INR',
      payment_method: 'Bank Transfer',
      receiving_bank: 'SBI',
      client_representative: '',
      representative_email: '',
      representative_phone: '',
      notes: ''
    });
    setDialogOpen(true);
  };

  const openEditDialog = (client) => {
    setSelectedClient(client);
    setFormData({
      client_name: client.client_name,
      active: client.active,
      type: client.type,
      web_design_amount: client.web_design_amount?.toString() || '',
      seo_amount: client.seo_amount?.toString() || '',
      google_ads_amount: client.google_ads_amount?.toString() || '',
      meta_ads_amount: client.meta_ads_amount?.toString() || '',
      contract_currency: client.contract_currency,
      payment_method: client.payment_method,
      receiving_bank: client.receiving_bank,
      client_representative: client.client_representative || '',
      representative_email: client.representative_email || '',
      representative_phone: client.representative_phone || '',
      notes: client.notes || ''
    });
    setDialogOpen(true);
  };

  const openNotesDialog = (notes) => {
    setSelectedNotes(notes || 'No notes available');
    setNotesDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.client_name) {
      toast.error('Client name is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        web_design_amount: formData.web_design_amount ? parseFloat(formData.web_design_amount) : 0,
        seo_amount: formData.seo_amount ? parseFloat(formData.seo_amount) : 0,
        google_ads_amount: formData.google_ads_amount ? parseFloat(formData.google_ads_amount) : 0,
        meta_ads_amount: formData.meta_ads_amount ? parseFloat(formData.meta_ads_amount) : 0
      };

      if (selectedClient) {
        await axios.put(`${API_URL}/clients/${selectedClient.id}`, payload);
        toast.success('Client updated successfully');
      } else {
        await axios.post(`${API_URL}/clients`, payload);
        toast.success('Client created successfully');
      }
      setDialogOpen(false);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save client');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedClient) return;

    try {
      await axios.delete(`${API_URL}/clients/${selectedClient.id}`);
      toast.success('Client deleted successfully');
      setDeleteDialogOpen(false);
      fetchClients();
    } catch (error) {
      toast.error('Failed to delete client');
    }
  };

  const [importMode, setImportMode] = useState('create');

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formDataObj = new FormData();
    formDataObj.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/import/clients?mode=${importMode}`, formDataObj, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const { created, updated, skipped, errors } = response.data;
      if (errors?.length > 0) {
        toast.warning(`Import: ${created} created, ${updated} updated, ${skipped} skipped, ${errors.length} errors`);
        console.log('Import errors:', errors);
      } else {
        toast.success(`Import complete: ${created} created, ${updated} updated, ${skipped} skipped`);
      }
      setImportDialogOpen(false);
      setImportMode('create');
      fetchClients();
    } catch (error) {
      toast.error('Import failed');
    }
  };

  const handleExport = async () => {
    try {
      const params = {};
      if (filters.active !== '') params.active = filters.active === 'true';
      if (filters.type) params.type = filters.type;

      const response = await axios.get(`${API_URL}/export/clients`, { params });
      downloadCSV(response.data, 'clients_export');
      toast.success('Export successful');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const downloadTemplate = () => {
    const templateData = [{
      client_name: 'Example Company',
      active: 'true',
      type: 'Client',
      web_design_amount: '5000',
      seo_amount: '2000',
      google_ads_amount: '2000',
      meta_ads_amount: '1000',
      contract_currency: 'USD',
      payment_method: 'WISE',
      receiving_bank: 'HDFC',
      client_representative: 'John Doe',
      representative_email: 'john@example.com',
      representative_phone: '+91 9876543210',
      notes: 'Sample client note'
    },
    {
      client_name: 'Partner Agency',
      active: 'true',
      type: 'Partner',
      web_design_amount: '0',
      seo_amount: '3000',
      google_ads_amount: '2000',
      meta_ads_amount: '0',
      contract_currency: 'GBP',
      payment_method: 'Bank Transfer',
      receiving_bank: 'SBI',
      client_representative: 'Jane Smith',
      representative_email: 'jane@partner.com',
      representative_phone: '+44 1234567890',
      notes: 'Partner referral agency'
    }];
    // Note: contract_amount is auto-calculated as sum of: web_design + seo + google_ads + meta_ads
    downloadCSV(templateData, 'clients_import_template');
    toast.success('Template downloaded with 2 example rows');
  };

  const clearFilters = () => {
    setFilters({ active: '', type: '', search: '' });
  };

  const getTypeBadge = (type) => {
    return <span className={`badge ${type === 'Client' ? 'badge-client' : 'badge-partner'}`}>{type}</span>;
  };

  const getStatusBadge = (active) => {
    return <span className={`badge ${active ? 'badge-active' : 'badge-inactive'}`}>{active ? 'Active' : 'Inactive'}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-6 animate-fade-in" data-testid="clients-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Clients</h1>
            <p className="text-muted-foreground">Manage your clients and partners</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {canEdit() && (
              <>
                <Button variant="outline" onClick={downloadTemplate} data-testid="download-client-template-btn">
                  <FileDown className="h-4 w-4 mr-2" />
                  Download Template
                </Button>
                <Button variant="outline" onClick={() => setImportDialogOpen(true)} data-testid="import-clients-btn">
                  <Upload className="h-4 w-4 mr-2" />
                  Import CSV
                </Button>
                <Button onClick={openCreateDialog} data-testid="add-client-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Client
                </Button>
              </>
            )}
            <Button variant="outline" onClick={handleExport} data-testid="export-clients-btn">
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Filters:</span>
              </div>

              <Select value={filters.active || "all"} onValueChange={(v) => setFilters({...filters, active: v === "all" ? "" : v})}>
                <SelectTrigger className="w-32" data-testid="filter-active">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="true">Active</SelectItem>
                  <SelectItem value="false">Inactive</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filters.type || "all"} onValueChange={(v) => setFilters({...filters, type: v === "all" ? "" : v})}>
                <SelectTrigger className="w-32" data-testid="filter-type">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {CLIENT_TYPES.map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search client..."
                  value={filters.search}
                  onChange={(e) => setFilters({...filters, search: e.target.value})}
                  className="pl-9 w-48"
                  data-testid="filter-search"
                />
              </div>

              {(filters.active || filters.type || filters.search) && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="h-4 w-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Clients Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-primary/5">
                    <TableHead>Client Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Contract Amount</TableHead>
                    <TableHead>Currency</TableHead>
                    <TableHead>Payment Method</TableHead>
                    <TableHead>Bank</TableHead>
                    <TableHead className="text-center">Contact</TableHead>
                    <TableHead className="text-center">Notes</TableHead>
                    {canEdit() && <TableHead className="text-center">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clients.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={canEdit() ? 10 : 9} className="text-center py-8 text-muted-foreground">
                        No clients found. {canEdit() && 'Click "Add Client" to create one.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    clients.map((client) => (
                      <TableRow key={client.id} className="clickable-row" data-testid={`client-row-${client.client_name}`}>
                        <TableCell className="font-medium">{client.client_name}</TableCell>
                        <TableCell>{getTypeBadge(client.type)}</TableCell>
                        <TableCell>{getStatusBadge(client.active)}</TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(client.contract_amount, client.contract_currency)}
                        </TableCell>
                        <TableCell>{client.contract_currency}</TableCell>
                        <TableCell>{client.payment_method}</TableCell>
                        <TableCell>{client.receiving_bank}</TableCell>
                        <TableCell className="text-center">
                          {(client.client_representative || client.representative_email || client.representative_phone) ? (
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <User className="h-4 w-4 text-primary" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-72">
                                <div className="space-y-2">
                                  <h4 className="font-semibold text-sm">Contact Details</h4>
                                  {client.client_representative && (
                                    <p className="text-sm"><span className="text-muted-foreground">Name:</span> {client.client_representative}</p>
                                  )}
                                  {client.representative_email && (
                                    <p className="text-sm"><span className="text-muted-foreground">Email:</span> {client.representative_email}</p>
                                  )}
                                  {client.representative_phone && (
                                    <p className="text-sm"><span className="text-muted-foreground">Phone:</span> {client.representative_phone}</p>
                                  )}
                                </div>
                              </PopoverContent>
                            </Popover>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {client.notes ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  onClick={() => openNotesDialog(client.notes)}
                                  className="h-8 w-8"
                                >
                                  <MessageSquare className="h-4 w-4 text-primary" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="max-w-xs">{client.notes.substring(0, 100)}{client.notes.length > 100 ? '...' : ''}</p>
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        {canEdit() && (
                          <TableCell>
                            <div className="flex items-center justify-center gap-1">
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                onClick={() => openEditDialog(client)}
                                data-testid={`edit-client-${client.client_name}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => { setSelectedClient(client); setDeleteDialogOpen(true); }}
                                data-testid={`delete-client-${client.client_name}`}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedClient ? 'Edit Client' : 'Add Client'}</DialogTitle>
              <DialogDescription>
                {selectedClient ? 'Update client details' : 'Enter client details to add a new client'}
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label>Client Name *</Label>
                <Input
                  placeholder="Enter client name"
                  value={formData.client_name}
                  onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                  data-testid="client-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Type *</Label>
                <Select value={formData.type} onValueChange={(v) => setFormData({...formData, type: v})}>
                  <SelectTrigger data-testid="client-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CLIENT_TYPES.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Service Amount Fields */}
              <div className="col-span-2 border rounded-lg p-4 space-y-4 bg-muted/30">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold">Service Amounts</Label>
                  <div className="text-sm text-primary font-mono font-semibold">
                    Total: {formatCurrency(calculateContractAmount(), formData.contract_currency)}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Web Design</Label>
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={formData.web_design_amount}
                      onChange={(e) => setFormData({...formData, web_design_amount: e.target.value})}
                      data-testid="client-web-design-amount"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">SEO</Label>
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={formData.seo_amount}
                      onChange={(e) => setFormData({...formData, seo_amount: e.target.value})}
                      data-testid="client-seo-amount"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Google Ads</Label>
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={formData.google_ads_amount}
                      onChange={(e) => setFormData({...formData, google_ads_amount: e.target.value})}
                      data-testid="client-google-ads-amount"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Meta Ads</Label>
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={formData.meta_ads_amount}
                      onChange={(e) => setFormData({...formData, meta_ads_amount: e.target.value})}
                      data-testid="client-meta-ads-amount"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Contract Currency</Label>
                <Select value={formData.contract_currency} onValueChange={(v) => setFormData({...formData, contract_currency: v})}>
                  <SelectTrigger data-testid="client-contract-currency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Payment Method *</Label>
                <Select value={formData.payment_method} onValueChange={(v) => setFormData({...formData, payment_method: v})}>
                  <SelectTrigger data-testid="client-payment-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map(m => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Receiving Bank *</Label>
                <Select value={formData.receiving_bank} onValueChange={(v) => setFormData({...formData, receiving_bank: v})}>
                  <SelectTrigger data-testid="client-receiving-bank">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RECEIVING_BANKS.map(b => (
                      <SelectItem key={b} value={b}>{b}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Representative Name</Label>
                <Input
                  placeholder="Contact person"
                  value={formData.client_representative}
                  onChange={(e) => setFormData({...formData, client_representative: e.target.value})}
                  data-testid="client-representative"
                />
              </div>

              <div className="space-y-2">
                <Label>Representative Email</Label>
                <Input
                  type="email"
                  placeholder="email@example.com"
                  value={formData.representative_email}
                  onChange={(e) => setFormData({...formData, representative_email: e.target.value})}
                  data-testid="client-email"
                />
              </div>

              <div className="space-y-2">
                <Label>Representative Phone</Label>
                <Input
                  placeholder="+91 XXXXX XXXXX"
                  value={formData.representative_phone}
                  onChange={(e) => setFormData({...formData, representative_phone: e.target.value})}
                  data-testid="client-phone"
                />
              </div>

              <div className="space-y-2 flex items-center gap-3">
                <Switch
                  checked={formData.active}
                  onCheckedChange={(checked) => setFormData({...formData, active: checked})}
                  data-testid="client-active-switch"
                />
                <Label>Active</Label>
              </div>

              <div className="col-span-2 space-y-2">
                <Label>Notes</Label>
                <Textarea
                  placeholder="Add any notes about this client..."
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  data-testid="client-notes"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-client-btn">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {selectedClient ? 'Update Client' : 'Save Client'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Notes Dialog */}
        <Dialog open={notesDialogOpen} onOpenChange={setNotesDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Client Notes</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm whitespace-pre-wrap">{selectedNotes}</p>
            </div>
            <DialogFooter>
              <Button onClick={() => setNotesDialogOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Import Dialog */}
        <Dialog open={importDialogOpen} onOpenChange={(open) => { setImportDialogOpen(open); if (!open) setImportMode('create'); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Import Clients</DialogTitle>
              <DialogDescription>
                Upload a CSV file with client data. Include &apos;id&apos; column to update existing clients.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div className="space-y-2">
                <Label>Import Mode</Label>
                <Select value={importMode} onValueChange={setImportMode}>
                  <SelectTrigger data-testid="client-import-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="create">Create Only (skip existing)</SelectItem>
                    <SelectItem value="upsert">Upsert (update existing by ID, create new)</SelectItem>
                    <SelectItem value="update_only">Update Only (skip new records)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {importMode === 'upsert' && 'Include "id" column from export to update existing clients'}
                  {importMode === 'create' && 'New clients will be created, existing ones skipped'}
                  {importMode === 'update_only' && 'Only rows with matching "id" will be updated'}
                </p>
              </div>
              <div className="import-dropzone">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleImport}
                  className="hidden"
                  id="client-import"
                />
                <label htmlFor="client-import" className="cursor-pointer">
                  <Upload className="h-10 w-10 mx-auto text-primary/50 mb-3" />
                  <p className="text-sm text-muted-foreground">
                    Click to select CSV file or drag and drop
                  </p>
                </label>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Client</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete &quot;{selectedClient?.client_name}&quot;? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-client">
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
